import {
  setCurrentPageName as setCurrentPageNameInGlobalTelemetry,
  setPageLoadNumber as setPageLoadNumberInGlobalTelemetry,
} from '@ms/yammer-telemetry-support';
import type { FC, PropsWithChildren } from 'react';
import React, { useCallback, useMemo, useRef, useState } from 'react';

import { emitPageLoadEvent } from '../../telemetry/loadEvents';
import { getPageLoadId, incrementPageLoadId } from '../../telemetry/pageLoadId';

import type { PageTelemetryContextProviderProps } from './PageTelemetryContextProvider.types';
import type { PageTelemetryContextValue, RouteProps } from './context';
import { PageTelemetryContext } from './context';
import { useReportHeroComponentRenderedCallback, useSetFirstLoadedPageSubscription } from './hooks';

interface ContextState extends RouteProps {
  readonly pathname?: string;
  readonly pageLoadId: number;
  readonly hasHeroComponentRendered: boolean;
}

const PageTelemetryContextProvider: FC<PropsWithChildren<PageTelemetryContextProviderProps>> = ({
  isRoutelessApp = false,
  children,
}) => {
  useSetFirstLoadedPageSubscription();
  const hasRenderedFirstRoute = useRef(false);

  const [routeProps, setRouteProps] = useState<ContextState>({
    pathname: undefined,
    pageLoadId: getPageLoadId(),
    path: undefined,
    page: 'Unknown',
    // oxlint-disable-next-line @ms/no-bad-route-config
    hero: ['none'],
    hasHeroComponentRendered: false,
  });

  // Ref to hold latest routeProps so callbacks can read current values without
  // being re-created on every route change (which caused a closure chain leak).
  // Updated eagerly inside setRoute when committing new state, so subsequent
  // calls (e.g. from child layout effects) always compare against correct values.
  const routePropsRef = useRef(routeProps);

  const setHeroComponentRendered = useCallback(() => {
    setRouteProps((prev) => {
      // istanbul ignore next
      if (prev.hasHeroComponentRendered) {
        return prev;
      }

      return { ...prev, hasHeroComponentRendered: true };
    });
  }, []);

  const setRoute = useCallback((props: RouteProps, pathname: string) => {
    const prev = routePropsRef.current;
    const hasRouteChanged =
      props.path !== prev.path || props.page !== prev.page || props.hero !== prev.hero || pathname !== prev.pathname;

    if (!hasRenderedFirstRoute.current) {
      hasRenderedFirstRoute.current = true;
    } else if (hasRouteChanged) {
      incrementPageLoadId();
      emitPageLoadEvent('RouteChanged');
    }

    const pageLoadId = getPageLoadId();
    const hasPageLoadIdChanged = pageLoadId !== prev.pageLoadId;
    const hasHeroComponentRendered = hasRouteChanged ? false : prev.hasHeroComponentRendered;
    if (hasRouteChanged || hasPageLoadIdChanged) {
      const nextState: ContextState = {
        ...props,
        pathname,
        pageLoadId,
        hasHeroComponentRendered,
      };

      // Eagerly update the ref so subsequent calls to setRoute (e.g. from a
      // child layout effect that fires before this parent's layout effect)
      // compare against the state we are about to commit, not the stale state.
      routePropsRef.current = nextState;
      setRouteProps(nextState);
    }
  }, []);

  setCurrentPageNameInGlobalTelemetry(routeProps.page);
  setPageLoadNumberInGlobalTelemetry(routeProps.pageLoadId);

  const reportHeroComponentRendered = useReportHeroComponentRenderedCallback({
    ...routeProps,
    isRoutelessApp,
    onHeroComponentRendered: setHeroComponentRendered,
  });

  const value = useMemo<PageTelemetryContextValue>(
    () => ({
      page: routeProps.page,
      path: routeProps.path,
      hero: routeProps.hero,
      pathname: routeProps.pathname,
      isRoutelessApp,
      setRoute,
      reportHeroComponentRendered,
      hasHeroComponentRendered: routeProps.hasHeroComponentRendered,
    }),
    [
      routeProps.page,
      routeProps.path,
      routeProps.hero,
      routeProps.pathname,
      isRoutelessApp,
      setRoute,
      reportHeroComponentRendered,
      routeProps.hasHeroComponentRendered,
    ]
  );

  return <PageTelemetryContext.Provider value={value}>{children}</PageTelemetryContext.Provider>;
};

export default PageTelemetryContextProvider;
